import { Button, ButtonGroup, Divider, ICrumb, Input, ISelectOption, Select, Switch } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import * as React from 'react';
import constants from '../../constants';
import { EmployeeDataSourceContainer } from '../../containers/pages/EmployeeDataSource';
import ContentConstants from '../../contentConstants';
import {
	AudienceSourceType,
	ExternalApplicationType,
	IBenchmarksActionCreators,
	ICulturesActionCreators,
	ISurveyActionCreators,
	ISurveyBuilderActionCreators,
	SurveyPropertyUnderValidation,
	UserType,
} from '../../models';
import { ITextToSpeechCulturesActionCreators } from '../../models/IActionCreators';
import { PublishDestination } from '../../models/PublishDestination';
import { IBenchmarksState } from '../../store/BenchmarksStore';
import { ICulturesState } from '../../store/CulturesStore';
import { IExternalHierarchyState } from '../../store/ExternalHierarchyStore';
import { ISurveyBuilderState } from '../../store/SurveyBuilderStore';
import { ISurveyState } from '../../store/SurveyStore';
import { ITextToSpeechCultureState } from '../../store/TextToSpeechCultureStore';
import { SurveyTypeEnumHelper, ValidationCollectionHelper } from '../../utilities';
import { CanUserAccessFeature } from '../../utilities/FeatureFlags';
import { BenchmarkSelector } from '../benchmarks/BenchmarkSelector';
import { CardLayout, Col3Col9, LabelAbove, SaveButtonsContainer, Section } from '../common';
import { SurveyNoteHeaderContainer } from '../notes/SurveyNoteHeaderContainer';
import { SurveyLanguages } from './basics/SurveyLanguages';
import { TextToSpeechWarning } from './basics/TextToSpeechWarning';

export type SurveyBasicsProps = {
	clientId?: number;
	userType: UserType;
} & IExternalHierarchyState &
	ICulturesState &
	IBenchmarksState &
	ISurveyState &
	ISurveyBuilderState &
	ITextToSpeechCultureState &
	ISurveyActionCreators &
	ISurveyBuilderActionCreators &
	IBenchmarksActionCreators &
	ICulturesActionCreators &
	ITextToSpeechCulturesActionCreators;

export interface ISurveyBasicsState {
	showBenchmarkModal: boolean;
}

export class SurveyBasics extends React.Component<SurveyBasicsProps, ISurveyBasicsState> {
	constructor(props: SurveyBasicsProps) {
		super(props);
		this.state = {
			showBenchmarkModal: false,
		};

		this.handleUpdateBenchmarkCrumbs = this.handleUpdateBenchmarkCrumbs.bind(this);
		this.toggleBenchmarkModal = this.toggleBenchmarkModal.bind(this);
		this.handleExternalApplicationType = this.handleExternalApplicationType.bind(this);
		this.handleSurveyTypes = this.handleSurveyTypes.bind(this);
		this.getPublishDestinations = this.getPublishDestinations.bind(this);
		this.handlePublishDestinationChange = this.handlePublishDestinationChange.bind(this);
	}

	private get LoadingEmployeeSourceInfo(): boolean {
		const { surveyEditModel } = this.props;
		const { audienceSourceType } = surveyEditModel.audienceSource;

		switch (audienceSourceType) {
			case AudienceSourceType.Hris: {
				const { hrisFiles } = this.props;

				return !!hrisFiles.isLoading;
			}
			case AudienceSourceType.Hierarchy: {
				const { hierarchyMetadatas } = this.props;

				return !!hierarchyMetadatas.isLoading;
			}
			default:
				return false;
		}
	}

	public componentDidMount() {
		this.props.GetCulturesIfNeeded();
		this.props.GetBenchmarksIfNeeded();
		this.props.SetDefaultState();
		this.props.GetExternalApplicationTypesIfNeeded();
		this.props.GetGlobalTopQuartileId();
		this.props.GetTextToSpeechSupportedCulturesIfNeeded();
	}

	public render() {
		const { surveyEditModel, validationResults, userType, HandleTextToSpeechChange } = this.props;
		const { benchmarks, supportedCultures } = this.props.surveyEditModel;

		const validationHelper = new ValidationCollectionHelper<SurveyPropertyUnderValidation>(validationResults);

		return (
			<CardLayout
				loading={this.props.isLoading || this.props.culturesLoading || this.props.benchmarksLoading}
				title={ContentConstants.TITLE_INITIAL_SURVEY_SETUP}
				header={<SurveyNoteHeaderContainer />}
			>
				<Section id='survey-name' sectionHeader={ContentConstants.SECTIONHEADERS_SURVEY_BASICS}>
					<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_NAME}>
						<LabelAbove type='input'>{ContentConstants.REFERENCE_ONLY_MESSAGE}</LabelAbove>
						<Input
							value={surveyEditModel.survey.name}
							onChange={this.props.HandleSurveyNameChange}
							error={!validationHelper.IsValid(SurveyPropertyUnderValidation.name)}
							message={validationHelper.Errors(SurveyPropertyUnderValidation.name)}
							id='survey-name'
							placeholder={ContentConstants.INPUT_PLACEHOLDER_NAME_YOUR_SURVEY_FILE}
							maxLength={150}
						/>
					</Col3Col9>
					<Divider type='dashed' />
					<SurveyLanguages />
					{userType === UserType.Internal && (
						<>
							<span>
								<Divider type='dashed' />
								<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_TEXTTOSPEECH}>
									<LabelAbove type='input'>{ContentConstants.SECTION_DESCRIPTION_TEXTTOSPEECH}</LabelAbove>
									{surveyEditModel.survey.isTextToSpeechEnabled && (
										<TextToSpeechWarning
											surveyCultures={supportedCultures}
											textToSpeechSupportedCultures={this.props.textToSpeechSupportedCultures}
										/>
									)}
									<Switch checked={surveyEditModel.survey.isTextToSpeechEnabled} getValue={HandleTextToSpeechChange as any} />
								</Col3Col9>
							</span>
						</>
					)}
				</Section>
				<Section
					id='survey-benchmarks'
					sectionHeader={
						<span>
							{ContentConstants.SECTIONHEADERS_BENCHMARKS + ' '}
							<span style={{ fontWeight: 'normal' }}>{ContentConstants.OPTIONAL_WITH_PARENS}</span>
						</span>
					}
				>
					<BenchmarkSelector
						{...this.props}
						selectedBenchmarkIds={benchmarks}
						globalTopQuartileId={this.props.globalTopQuartileId}
						descriptionText={ContentConstants.SECTION_DESCRIPTION_BENCHMARKS}
					/>
				</Section>
				<Section id='employee-data-source' sectionHeader={ContentConstants.SECTIONHEADERS_EMPLOYEE_DATA_SOURCE}>
					<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_EMPLOYEE_DATA_SOURCE}>
						<LabelAbove type='input'>{ContentConstants.SECTION_DESCRIPTION_EMPLOYEE_DATA_SOURCE}</LabelAbove>
						<EmployeeDataSourceContainer />
					</Col3Col9>
				</Section>
				{this.getReportingApplicationSection()}
				{CanUserAccessFeature(undefined, 'EnablePublishDestination') && (
					<Section id='employee-data-source' sectionHeader={ContentConstants.SECTIONHEADERS_PUBLISH_DESTINATION}>
						<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_PUBLISH_DESTINATION}>
							<LabelAbove type='input'>{ContentConstants.SECTION_DESCRIPTION_PUBLISH_DESTINATION}</LabelAbove>
							<Select
								displayText={ContentConstants.PUBLISH_CHOOSE_YOUR_DESTINATION}
								selectSize='xlg'
								items={this.getPublishDestinations()}
								selectedValue={PublishDestination[this.props.surveyEditModel.survey.publishDestination]}
								onApply={this.handlePublishDestinationChange}
							/>
						</Col3Col9>
					</Section>
				)}
				<Divider type='dashed' />
				<SaveButtonsContainer>
					<ButtonGroup type='column'>
						<Button
							id='SaveButton'
							color='secondary'
							size='lg'
							onClick={() => this.props.Save(false)}
							disabled={this.props.saving || this.LoadingEmployeeSourceInfo}
						>
							{ContentConstants.BUTTONS_SAVE}
						</Button>
						<Button
							id='SaveAndNextStepButton'
							color='primary'
							size='lg'
							onClick={() => this.props.Save(true)}
							disabled={this.props.saving || this.LoadingEmployeeSourceInfo}
						>
							{ContentConstants.BUTTONS_SAVE_AND_NEXT}
						</Button>
					</ButtonGroup>
				</SaveButtonsContainer>
			</CardLayout>
		);
	}

	private getPublishDestinations(): ISelectOption[] {
		return [
			{
				name: PublishDestination[PublishDestination.mResearch],
				displayText: PublishDestination[PublishDestination.mResearch],
				displayOrder: PublishDestination.mResearch,
			},
			{
				name: PublishDestination[PublishDestination.SurveyEngine],
				displayText: PublishDestination[PublishDestination.SurveyEngine],
				displayOrder: PublishDestination.SurveyEngine,
			},
		];
	}

	private toggleBenchmarkModal() {
		const visibility = !this.state.showBenchmarkModal;
		this.setState({ showBenchmarkModal: visibility });
	}

	private handleUpdateBenchmarkCrumbs(benchmarks: ICrumb[]) {
		const benchmarkIds = _.map(benchmarks, (b: ICrumb) => Number(b.value));
		this.props.UpdateSelectedBenchmarks(benchmarkIds);
	}

	private handlePublishDestinationChange(destination?: string | string[]) {
		if (destination !== undefined) {
			this.props.HandlePublishDestination(PublishDestination[destination as string]);
		}
	}

	private handleExternalApplicationType(value?: string | string[]) {
		let applicationType: ExternalApplicationType | undefined;
		if (value && value !== '') {
			applicationType = Number(value);
		}
		this.props.HandleExternalApplicationType(applicationType);
	}

	private handleSurveyTypes(value: string | string[] | undefined) {
		if (value !== undefined) {
			if (Array.isArray(value)) {
				this.props.HandleSurveyTypes(value);
			} else {
				this.props.HandleSurveyTypes(Array.from(value));
			}
		}
	}

	private getReportingApplicationSection() {
		const { applicationTypes, clientId, userType, validationResults } = this.props;

		if (userType == UserType.Internal) {
			const validationHelper = new ValidationCollectionHelper<SurveyPropertyUnderValidation>(validationResults);
			const reportingApplicationFragment = (
				<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_REPORTING_APPLICATION}>
					<LabelAbove type='input'>{ContentConstants.SECTION_DESCRIPTION_REPORTING_APPLICATION}</LabelAbove>
					<Select
						className='application-type'
						displayText={ContentConstants.REPORTING_APPLICATION_CHOOSE_YOUR_SOURCE}
						selectSize='xlg'
						items={applicationTypes}
						error={!validationHelper.IsValid(SurveyPropertyUnderValidation.externalApplicationType)}
						message={validationHelper.Errors(SurveyPropertyUnderValidation.externalApplicationType)}
						selectedValue={this.props.surveyEditModel.survey.externalApplicationType?.toString()}
						onApply={this.handleExternalApplicationType}
						disabled={this.props.surveyId !== 0}
					/>
				</Col3Col9>
			);
			let surveyTypesFragment = <></>;
			if (clientId !== undefined && clientId === constants.SYSTEM_CLIENT_ID) {
				const surveyTypes = SurveyTypeEnumHelper.getSurveyTypeAsSelectOptions();
				surveyTypesFragment = (
					<>
						<Divider type='dashed' />
						<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_SURVEY_TYPE}>
							<LabelAbove type='input'>{ContentConstants.SECTION_DESCRIPTION_SURVEY_TYPE}</LabelAbove>
							<Select
								className='survey-type'
								displayText={ContentConstants.REPORTING_APPLIACITON_CHOOSE_SURVEY_TYPES}
								items={surveyTypes}
								error={!validationHelper.IsValid(SurveyPropertyUnderValidation.surveyTypes)}
								message={validationHelper.Errors(SurveyPropertyUnderValidation.surveyTypes)}
								multiple={true}
								onApply={this.handleSurveyTypes}
								selectSize='xlg'
								selectedValues={SurveyTypeEnumHelper.getSurveyTypeStringValues(this.props.surveyEditModel.survey.surveyTypes)}
							/>
						</Col3Col9>
					</>
				);
			}
			return (
				<Section id='reporting-application' sectionHeader={ContentConstants.SECTIONHEADERS_REPORTING_APPLICATION}>
					{reportingApplicationFragment}
					{surveyTypesFragment}
				</Section>
			);
		}
		return <></>;
	}
}
